import { useState } from "react";
import axios from "axios";
import { STATS_REFRESH_EVENT } from "../hooks/useFoundingStats";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WaitlistForm({ source = "homepage", variant = "light" }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [result, setResult] = useState(null);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      const { data } = await axios.post(`${API}/waitlist`, { ...form, source });
      setResult(data);
      setStatus("success");
      setForm({ first_name: "", last_name: "", email: "", message: "" });
      // Refresh the global Founding 50 counter
      window.dispatchEvent(new Event(STATS_REFRESH_EVENT));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(
        Array.isArray(detail)
          ? detail.map((d) => d.msg).join(" · ")
          : detail || "Something went wrong. Please try again."
      );
      setStatus("error");
    }
  };

  const onDark = variant === "dark";

  if (status === "success") {
    const isFounding = result?.founding;
    const position = result?.position;
    return (
      <div
        data-testid="waitlist-success"
        className="text-center py-16 px-6 rounded-md"
        style={{
          background: onDark ? "rgba(250,247,242,0.06)" : "var(--white)",
          border: `1px solid ${onDark ? "rgba(250,247,242,0.18)" : "var(--line)"}`,
        }}
      >
        <div
          className="w-14 h-14 rounded-full inline-flex items-center justify-center mb-5"
          style={{ background: "var(--gold)", color: "var(--forest)" }}
        >
          <i className="fa-solid fa-check text-xl"></i>
        </div>
        {isFounding && position ? (
          <>
            <p
              className="eyebrow mb-3 inline-flex items-center gap-2"
              style={{ color: onDark ? "var(--gold-soft)" : "var(--gold-deep)" }}
            >
              <i className="fa-solid fa-leaf text-xs"></i>
              You are Founding #{position} of 50
            </p>
            <h3
              className="heading-display text-3xl mb-3"
              style={{ color: onDark ? "var(--cream)" : "var(--forest)" }}
            >
              Welcome, <span className="heading-italic">founder</span>.
            </h3>
            <p
              className="text-sm max-w-md mx-auto"
              style={{ color: onDark ? "rgba(250,247,242,0.82)" : "var(--brown-soft)" }}
            >
              Ramona will write to you personally in the coming weeks. You have a first pick on opening week 2026 and a founding rate honoured for every future stay.
            </p>
          </>
        ) : (
          <>
            <h3
              className="heading-display text-3xl mb-3"
              style={{ color: onDark ? "var(--cream)" : "var(--forest)" }}
            >
              Thank you — we'll be in touch!
            </h3>
            <p
              className="text-sm max-w-md mx-auto"
              style={{ color: onDark ? "rgba(250,247,242,0.78)" : "var(--brown-soft)" }}
            >
              {position && position > 50
                ? `You're #${position} on the Casa Haya waitlist. We'll reach out personally when public availability opens in 2026.`
                : "We've added you to the waitlist for Casa Haya. You'll hear from us personally when stays open in 2026."}
            </p>
          </>
        )}
        <button
          data-testid="waitlist-reset"
          onClick={() => { setStatus("idle"); setResult(null); }}
          className="mt-7 text-xs uppercase tracking-[0.2em] link-underline"
          style={{ color: onDark ? "var(--gold-soft)" : "var(--gold-deep)" }}
        >
          Register another →
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="waitlist-form"
      onSubmit={submit}
      className="grid gap-5"
      noValidate
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="label-field" style={onDark ? { color: "rgba(250,247,242,0.7)" } : {}}>
            First name
          </label>
          <input
            data-testid="waitlist-first-name"
            type="text"
            name="first_name"
            required
            value={form.first_name}
            onChange={onChange}
            className="input-field"
            placeholder="Jane"
          />
        </div>
        <div>
          <label className="label-field" style={onDark ? { color: "rgba(250,247,242,0.7)" } : {}}>
            Last name
          </label>
          <input
            data-testid="waitlist-last-name"
            type="text"
            name="last_name"
            required
            value={form.last_name}
            onChange={onChange}
            className="input-field"
            placeholder="Doe"
          />
        </div>
      </div>

      <div>
        <label className="label-field" style={onDark ? { color: "rgba(250,247,242,0.7)" } : {}}>
          Email address
        </label>
        <input
          data-testid="waitlist-email"
          type="email"
          name="email"
          required
          value={form.email}
          onChange={onChange}
          className="input-field"
          placeholder="you@email.com"
        />
      </div>

      <div>
        <label className="label-field" style={onDark ? { color: "rgba(250,247,242,0.7)" } : {}}>
          Optional message
        </label>
        <textarea
          data-testid="waitlist-message"
          name="message"
          rows={4}
          value={form.message}
          onChange={onChange}
          className="input-field resize-none"
          placeholder="Tell us about your ideal stay — group size, season, anything else..."
        />
      </div>

      {status === "error" && (
        <p
          data-testid="waitlist-error"
          className="text-sm"
          style={{ color: "#a04040" }}
        >
          {errorMsg}
        </p>
      )}

      <button
        data-testid="waitlist-submit"
        type="submit"
        disabled={status === "loading"}
        className="btn-primary justify-center mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "loading" ? (
          <>
            <i className="fa-solid fa-circle-notch fa-spin"></i> Sending...
          </>
        ) : (
          <>
            Register Interest <i className="fa-solid fa-arrow-right text-sm"></i>
          </>
        )}
      </button>

      <p
        className="text-xs text-center mt-2"
        style={{ color: onDark ? "rgba(250,247,242,0.55)" : "var(--brown-soft)" }}
      >
        We respect your privacy. Submissions are sent to{" "}
        <span className="font-medium">info@casahaya.com</span>.
      </p>
    </form>
  );
}
